import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { CREW_RESOURCE } from '../crew/types';
import { DateQuestionDialog } from '../requests/DateQuestionDialog';
import { countText } from '../requests/recipients';
import { REQUESTS_RESOURCE, UNSENT_KEY } from '../requests/types';
import { DialogLayer } from '../ui/Dialog';
import { useLeavePrompt } from '../ui/unsaved';
import { dayMonth } from './peopleGrid';

/**
 * One person not asked yet: `name` absent — hidden from the role (rule 7a); `days` absent — the dates hidden (review
 * I1); `noContact` — no account, e-mail or phone: listed, not counted, never asked (proposal 2).
 */
export type UnsentPerson = { personId: string; name?: string; days?: string[]; noContact?: true };
/** One event of „Do wysłania”: `count` — those a question reaches; `people` absent — the field „Osoba” hidden (only how many). */
export type UnsentEvent = { eventId: string; eventName: string; count: number; noContactCount?: number; days?: string[]; people?: UnsentPerson[] };
type UnsentData = { total?: number; events?: UnsentEvent[] };

const daysText = (days: readonly string[] | undefined) => (days ?? []).map(dayMonth).join(', ');
/** „Wesele A, 7.11” — or only the event's name when the days are hidden. */
const where = (eventName: string, days: readonly string[] | undefined) => (days && days.length > 0 ? `${eventName}, ${daysText(days)}` : eventName);
/** „Jan Kowalski · 7.11 · Wesele A” — the days left out when hidden. */
const line = (who: string, days: readonly string[] | undefined, eventName: string) => [who, ...(days && days.length > 0 ? [daysText(days)] : []), eventName].join(' · ');
/** Every person of the event named (the field „Osoba” and „Osoby: Podgląd”): each by name; else how many. */
const named = (e: UnsentEvent): e is UnsentEvent & { people: Array<UnsentPerson & { name: string }> } => !!e.people && e.people.length > 0 && e.people.every((p) => !!p.name);
/** The people a question can go to now: a contact and their days known. */
const askable = (e: UnsentEvent) => (e.people ?? []).filter((p): p is UnsentPerson & { days: string[] } => !p.noContact && !!p.days && p.days.length > 0);

/** „Nie wysłano zapytań do: Jan Kowalski (Wesele A, 7.11), …. Wysłać teraz?” (owner 2026-10-27 12:41); those without contact are not among them. */
export function unsentText(events: readonly UnsentEvent[], canSend: boolean): string {
  const items = events.flatMap((e) => {
    if (named(e)) return e.people.filter((p) => !p.noContact).map((p) => `${p.name} (${where(e.eventName, p.days)})`);
    return e.count > 0 ? [`${countText(e.count, 'genitive')} (${where(e.eventName, e.days)})`] : [];
  });
  return `Nie wysłano zapytań do: ${items.join(', ')}.${canSend ? ' Wysłać teraz?' : ''}`;
}

/** Whether going to `to` leaves „Dostępność → Ludzie” (another screen, or the tab „Sprzęt”); the side panel does not. */
export function leavesPeople(to: string): boolean {
  const url = new URL(to, 'http://app.invalid');
  return url.pathname.replace(/\/+$/, '') !== '/dostepnosc' || url.searchParams.get('widok') === 'sprzet';
}

/**
 * Owner 2026-10-27 12:41 / 14:08 („tak robić”): over the grid „Do wysłania: N” — the people put in crews as
 * „Proponowany” who were not asked about that event yet (GET /api/requests/unsent: events from today on; rights and
 * rule 7a there). Its list: person · days · event (without contact: „brak kontaktu”, not counted); per event „Wyślij
 * teraz” (the question pinned to the event, those people with their own days, one question each — after it the counter
 * reads again) and „Otwórz panel” (the event's side panel). Leaving „Dostępność → Ludzie” asks first (another screen,
 * the tab „Sprzęt”; closing the browser tab — the browser's own question) — only while the user can act on an entry
 * (review M3); the list emptied meanwhile lets the leaving go on (M4); „Wyślij teraz” from that question goes on after
 * the send (proposal 3). Nothing is ever sent by itself.
 */
export function Unsent({ onOpenPanel }: { onOpenPanel: (eventId: string) => void }) {
  const { me } = useMe();
  const mayRead = !!me && can(me, CREW_RESOURCE, 'view');
  const query = useQuery({ queryKey: UNSENT_KEY, enabled: mayRead, queryFn: () => api<UnsentData>('/api/requests/unsent') });
  const events = mayRead ? (query.data?.events ?? []) : [];
  const total = mayRead ? (query.data?.total ?? 0) : 0;
  // the question is pinned to the event: „Zapytania o dostępność: Dodawanie” and „Obsada: Dodawanie” (as the server)
  const canSend = !!me && can(me, REQUESTS_RESOURCE, 'create') && can(me, CREW_RESOURCE, 'create');
  const sendable = (e: UnsentEvent) => canSend && askable(e).length > 0;
  const actionable = events.some(sendable);
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState<{ go: () => void } | null>(null);
  // `then`: proposal (3) — where the user was going, after the send
  const [asking, setAsking] = useState<{ event: UnsentEvent; then?: () => void } | null>(null);
  const sent = useRef(false);
  useLeavePrompt(actionable, { leaves: leavesPeople, ask: (go) => setLeaving({ go }) });

  // review M4: nothing left to act on while the question is open — it closes and the leaving goes on
  useEffect(() => {
    if (!leaving || actionable) return;
    setLeaving(null);
    leaving.go();
  }, [leaving, actionable]);

  const send = (e: UnsentEvent, then?: () => void) => {
    setLeaving(null);
    sent.current = false;
    setAsking({ event: e, ...(then ? { then } : {}) });
  };
  const panel = (e: UnsentEvent) => {
    setLeaving(null);
    onOpenPanel(e.eventId);
  };

  return (
    <>
      {total > 0 && (
        <div className="pg-unsent" data-feature-id="requests.unsent">
          <button className="btn small pg-unsent-count" type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
            Do wysłania: {total}
          </button>
          {open && (
            <section className="pg-unsent-list" aria-label="Do wysłania">
              {events.map((e) => (
                <div key={e.eventId} className="pg-unsent-event">
                  <ul>
                    {named(e) ? (
                      e.people.map((p) => (
                        <li key={p.personId}>
                          {line(p.name ?? '', p.days, e.eventName)}
                          {p.noContact && ' — brak kontaktu'}
                        </li>
                      ))
                    ) : (
                      <li>
                        {line(countText(e.count), e.days, e.eventName)}
                        {e.noContactCount ? ` — brak kontaktu: ${countText(e.noContactCount)}` : ''}
                      </li>
                    )}
                  </ul>
                  <div className="actions">
                    {sendable(e) && (
                      <button className="btn small" type="button" aria-label={`Wyślij teraz: ${e.eventName}`} onClick={() => send(e)}>
                        Wyślij teraz
                      </button>
                    )}
                    <button className="btn ghost small" type="button" aria-label={`Otwórz panel: ${e.eventName}`} onClick={() => panel(e)}>
                      Otwórz panel
                    </button>
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>
      )}
      {leaving && actionable && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="unsent-title" aria-describedby="unsent-text" data-feature-id="requests.unsent">
            <DialogLayer onClose={() => setLeaving(null)} />
            <h2 id="unsent-title">Zapytania nie zostały wysłane</h2>
            <p id="unsent-text">{unsentText(events, true)}</p>
            <ul className="pg-unsent-leave">
              {events
                .filter((e) => e.count > 0)
                .map((e, i) => (
                  <li key={e.eventId}>
                    <strong>{e.eventName}</strong>
                    <span className="actions">
                      {sendable(e) && (
                        <button className="btn small primary" type="button" autoFocus={i === 0} aria-label={`Wyślij teraz: ${e.eventName}`} onClick={() => send(e, leaving.go)}>
                          Wyślij teraz
                        </button>
                      )}
                      <button className="btn small" type="button" aria-label={`Przejdź do panelu wydarzenia: ${e.eventName}`} onClick={() => panel(e)}>
                        Przejdź do panelu wydarzenia
                      </button>
                    </span>
                  </li>
                ))}
            </ul>
            <div className="actions">
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  const go = leaving.go;
                  setLeaving(null);
                  go();
                }}
              >
                Później
              </button>
            </div>
          </div>
        </div>
      )}
      {asking && (
        <DateQuestionDialog
          title="Zapytanie o dostępność"
          // review I1 (#203): one question per person, their own days only; listed before „Wyślij”
          perPerson={askable(asking.event).map((p) => ({ personId: p.personId, name: p.name, days: p.days }))}
          event={{ id: asking.event.eventId, name: asking.event.eventName }}
          onSent={() => {
            sent.current = true;
          }}
          onClose={() => {
            const then = sent.current ? asking.then : undefined;
            setAsking(null);
            then?.();
          }}
        />
      )}
    </>
  );
}
